import { parseTaskInstant } from './taskTimezoneUtils';

const MAX_TIMER_DELAY = 2_147_000_000;

export const DEFAULT_REMINDER_CONFIG = Object.freeze({
  enabled: false,
  browserNotification: true,
  sound: true,
  soundType: 'gentle-chime',
  soundVolume: 65,
  soundRepeatMode: 'once',
  soundDurationSeconds: null,
  offsetsMinutes: [15],
});

export function normalizeReminderConfig(value = {}) {
  const source = value || {};
  const offsets = source.offsetsMinutes || source.offsets_minutes || [15];
  return {
    enabled: Boolean(source.enabled),
    browserNotification: source.browserNotification ?? source.browser_notification ?? true,
    sound: source.sound ?? true,
    soundType: source.soundType || source.sound_type || 'gentle-chime',
    soundVolume: Number(source.soundVolume ?? source.sound_volume ?? 65),
    soundRepeatMode: source.soundRepeatMode || source.sound_repeat_mode || 'once',
    soundDurationSeconds:
      source.soundDurationSeconds ?? source.sound_duration_seconds ?? null,
    offsetsMinutes: [...new Set((offsets || []).map(Number))]
      .filter((offset) => Number.isInteger(offset) && offset >= 0)
      .sort((a, b) => a - b)
      .slice(0, 10),
  };
}

export function applyReminderPriorityDefault(reminders, priority) {
  if (reminders.soundRepeatExplicit) return reminders;
  return {
    ...reminders,
    soundRepeatMode: priority === 'urgent' ? 'duration' : 'once',
    soundDurationSeconds: priority === 'urgent' ? 30 : null,
  };
}

export class TaskReminderScheduler {
  constructor({
    onFire,
    now = () => Date.now(),
    setTimer = (callback, delay) => window.setTimeout(callback, delay),
    clearTimer = (timer) => window.clearTimeout(timer),
  }) {
    this.onFire = onFire;
    this.now = now;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.timers = new Map();
    this.fired = new Set();
  }

  _key(task, offset) {
    return `${task.taskId}:${offset}`;
  }

  _signature(task, reminder, offset) {
    return JSON.stringify([
      task.dueAt,
      task.status,
      offset,
      reminder.browserNotification,
      reminder.sound,
      reminder.soundType,
      reminder.soundVolume,
      reminder.soundRepeatMode,
      reminder.soundDurationSeconds,
      task.title,
    ]);
  }

  _schedule(key, targetTime, task, reminder, offset, signature) {
    const remaining = targetTime - this.now();
    if (remaining <= 0 || this.fired.has(key)) return;
    const delay = Math.min(remaining, MAX_TIMER_DELAY);
    const timer = this.setTimer(() => {
      this.timers.delete(key);
      if (targetTime - this.now() > 0) {
        this._schedule(key, targetTime, task, reminder, offset, signature);
        return;
      }
      if (this.fired.has(key)) return;
      this.fired.add(key);
      this.onFire?.({ task, reminder, offset, key });
    }, delay);
    this.timers.set(key, { timer, signature });
  }

  reconcile(tasks = []) {
    const desired = new Set();

    tasks.forEach((task) => {
      const reminder = normalizeReminderConfig(task.reminders);
      const dueTime = parseTaskInstant(task.dueAt).getTime();
      if (
        task.status === 'completed'
        || task.deletedAt
        || !reminder.enabled
        || !Number.isFinite(dueTime)
      ) return;

      reminder.offsetsMinutes.forEach((offset) => {
        const key = this._key(task, offset);
        const targetTime = dueTime - (offset * 60_000);
        if (targetTime <= this.now()) return;
        desired.add(key);
        const signature = this._signature(task, reminder, offset);
        const existing = this.timers.get(key);
        if (existing?.signature === signature) return;
        if (existing) this.clearTimer(existing.timer);
        this._schedule(key, targetTime, task, reminder, offset, signature);
      });
    });

    this.timers.forEach((entry, key) => {
      if (!desired.has(key)) {
        this.clearTimer(entry.timer);
        this.timers.delete(key);
      }
    });
  }

  snooze(task, reminder, minutes = 5) {
    const key = `${task.taskId}:snooze:${this.now()}`;
    this._schedule(
      key,
      this.now() + (minutes * 60_000),
      task,
      reminder,
      -minutes,
      `snooze:${minutes}`,
    );
  }

  cancelTask(taskId) {
    this.timers.forEach((entry, key) => {
      if (key.startsWith(`${taskId}:`)) {
        this.clearTimer(entry.timer);
        this.timers.delete(key);
      }
    });
  }

  destroy() {
    this.timers.forEach((entry) => this.clearTimer(entry.timer));
    this.timers.clear();
  }
}
